/**
 * Consolidated Import Mixin
 * Combines all import functionality into a single, maintainable mixin
 * Eliminates the need for multiple mixins and complex state management
 */
export default {
    data() {
        return {
            // Import state
            initial_load_complete: true,
            import_id: null,
            import_meta_data: null,
            import_state: 'new',
            import_definition: null,
            sheet_information: null,
            preview_data: null,

            // UI state
            current_step: 1,

            // Upload state
            excel_file: null,
            uploading: false,
            upload_progress_percent: 0,
            upload_progress_message: '',
            upload_process_id: null,
            upload_errors: [],

            //Intermediate sheet data
            intermediate_process_errors: [],

            // Preview state
            generating_preview: false,
            preview_ready: false,
            preview_failed: false,
            preview_error_message: '',

            // Import execution state
            importing: false,
            import_progress_percent: 0,
            import_progress_message: '',
            import_process_id: null,
            execution_errors: [],

            // Column mapping state
            can_continue_to_preview: false,
            suggestedMapping: {},
            loading_sheet_information: false,

            // Phone validation state (for contact imports)
            validating_phone_numbers: false,
            phone_validation_batch_id: null,
            phone_validation_status: '',
            phone_validation_polling_interval: null,
            used_phone_validation: false,

            // Row selection state (for imports that support row selection)
            selected_rows: [],
            select_all: false,

            // Column mapping state
            column_mapping_data: null,
            saving_column_mapping: false,
            intermediate_process_id: null,

        };
    },

    computed: {
        /**
         * Import type - must be implemented by child component
         */
        importType() {
            throw new Error('importType must be implemented by child component');
        },

        /**
         * Page title
         */
        pageTitle() {
            if (this.import_id) {
                return `${this.getImportTypeDisplayName()} Import - ${this.import_id}`;
            }
            return `${this.getImportTypeDisplayName()} Import`;
        },

        /**
         * Sheet name from import definition
         */
        sheetName() {
            if (this.import_definition && this.import_definition.sheet_name) {
                return this.import_definition.sheet_name;
            }
            return null;
        },

        /**
         * Preview table title with sheet name
         */
        previewTableTitle() {
            if (this.sheetName) {
                return `Chosen Sheet to Import: '${this.sheetName}'`;
            }
            else {
                return `${this.getImportTypeDisplayName()}  Import Preview`;
            }
        },
        hasWriteAccess() {
            // Updated: RedChirp staff can write even on real customer accounts (with UI warning elsewhere)
            const organization_id = this.$route.params.organization_id;

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                return true;
            } else if (this.$rc.$state.isOrganizationAdmin(organization_id)) {
                return true;
            }
            return false;
        },

        isReadOnlyMode() {
            // User is in read-only mode if they have read access but not write access
            // Backend won't return import details if user doesn't have read access
            return !this.hasWriteAccess;
        },


        /**
         * Check if import can be cancelled
         */
        canCancelImport() {
            return ['uploaded', 'preview-ready'].includes(this.import_state);
        },

        /**
         * Check if can proceed to attestation
         */
        canProceedToAttestation() {
            return this.preview_ready && !this.hasErrorsPreventingImport;
        },

        /**
         * Check if has errors preventing import
         */
        hasErrorsPreventingImport() {
            return this.preview_data?.global_validation_errors?.length > 0;
        },

        // === ROW SELECTION COMPUTED PROPERTIES ===

        /**
         * Get importable rows (rows that can be selected for import)
         */
        importableRows() {
            if (!this.preview_data || !this.preview_data.preview_rows) {
                return [];
            }
            return this.preview_data.preview_rows.filter(row => row.will_be_imported);
        },

        /**
         * Check if all importable rows are selected
         */
        allRowsSelected() {
            if (this.importableRows.length === 0) {
                return false;
            }
            return this.selected_rows.length === this.importableRows.length;
        },

        /**
         * Check if some (but not all) importable rows are selected
         */
        someRowsSelected() {
            return this.selected_rows.length > 0 && this.selected_rows.length < this.importableRows.length;
        }
    },

    methods: {
        /**
         * Initialize component
         */
        initializeComponent() {
            this.import_id = this.$route.params.import_id;
            if (this.import_id) {
                this.initial_load_complete = false;
                //After this completes, initial_load_complete will be set to true
                this.loadExistingImport();
            }
        },

        /**
         * Load existing import data
         * 
         * TODO The API call for this needs to be refactored so it can be better shared for state management.
         */
        loadExistingImport() {
            this.$rc.rest({
                'endpoint': `GET: /api/v1/imports/${this.importType}/details/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    this.import_meta_data = data.import_meta_data;
                    this.import_state = data.import_meta_data.state;
                    this.initial_load_complete = true;
                    // Load import definition if available (needed for column mapping)
                    if (data.import_meta_data?.raw_import_definition) {
                        this.import_definition = data.import_meta_data.raw_import_definition;
                    }

                    // Load enriched data if available
                    const enriched_data = data.enriched_data;
                    if (enriched_data) {
                        this.preview_data = enriched_data;
                        this.preview_ready = true;
                    }

                    this.handleImportStateTransition();
                },
                'on_error': (error) => {
                    this.initial_load_complete= true;
                    this.preview_failed = true;
                    this.preview_error_message = `Failed to load import: ${error}`;
                    // Only redirect if this is a critical error (like import not found)
                    // Don't redirect for temporary network issues
                    if (error && (error.toString().includes('not found') || error.toString().includes('404'))) {
                        this.goBackToImportSearch();
                    }
                }
            });
        },

        /**
         * Handle import state transitions
         * This method should be implemented by each import type to handle their specific step logic
         */
        handleImportStateTransition() {
            // This method should be overridden by components using this mixin
            // Each import type has unique step mappings and state handling requirements
            // If you see this comment in the browser console, the component needs to implement handleImportStateTransition
        },

        /**
         * Load preview data from backend
         */
        loadPreviewData() {
            this.generating_preview = true;
            this.preview_failed = false;
            this.preview_ready = false;

            this.$rc.rest({
                'spinner': false,
                'endpoint': `GET: /api/v1/imports/${this.importType}/details/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    const enriched_data = data.enriched_data;
                    if (enriched_data) {
                        this.preview_data = enriched_data;
                        this.preview_ready = true;
                        this.preview_failed = false;
                    } else {
                        this.onPreviewFailed({ exception: 'No preview data available' });
                    }
                    this.generating_preview = false;
                },
                'on_error': (error) => {
                    this.onPreviewFailed({ exception: `Failed to load preview data: ${error}` });
                }
            });
        },

        /**
         * Handle preview failure
         */
        onPreviewFailed(error) {
            this.generating_preview = false;
            this.preview_failed = true;
            this.preview_error_message = error.exception || error.message || error;
        },

        /**
         * Start preview generation
         */
        startPreviewGeneration() {
            this.loadPreviewData();
        },

        /**
         * Load sheet information
         */
        loadSheetInformation() {
            this.loading_sheet_information = true;

            // If we have a draft mapping choice for header row, send it along so preview sampling matches UI
            const hasHeaderRow = this.column_mapping_data?.has_header_row;

            this.$rc.rest({
                'spinner': false,
                'endpoint': `GET: /api/v1/imports/${this.importType}/sheet-information/${this.$route.params.organization_id}/${this.import_id}`,
                'params': hasHeaderRow === undefined ? {} : { has_header_row: hasHeaderRow },
                'on_success': (data) => {
                    this.sheet_information = data;
                    this.loading_sheet_information = false;
                },
                'on_error': (error) => {
                    this.loading_sheet_information = false;
                    this.preview_failed = true;
                    this.intermediate_process_errors = [error.message || error];
                }
            });
        },

        /**
         * Get import type display name
         */
        getImportTypeDisplayName() {
            //Find all dashes, replace with space, and capitalize letters
            const prettyType = this.importType.replace(/-/g, ' ');
            return prettyType.replace(/\b\w/g, l => l.toUpperCase());
        },

        /**
         * Go back to import search
         */
        goBackToImportSearch() {
            this.$router.push(`/organizations/${this.$route.params.organization_id}/imports/${this.importType}/search`);
        },

        /**
         * Navigate to step
         */
        goToStep(step) {
            if (this.canEditStep(step)) {
                this.current_step = step;
            }
        },

        // === UPLOAD FUNCTIONALITY ===

        /**
         * Handle file upload
         */
        onClickUploadFile() {
            this.uploading = true;
            this.upload_errors = [];

            const form_data = new FormData();
            form_data.append(this.importType + "_file", this.excel_file);
            form_data.append('original_file_name', this.excel_file.name);
            form_data.append('import_type', this.importType);

            // Add any additional form data
            this.addAdditionalUploadData(form_data);

            this.$rc.makeApiCall({
                url: this.getUploadUrl(),
                method: 'post',
                form_data: form_data,
                timeout: 2 * 60 * 1000, // 2 minutes
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: (data) => {
                        this.upload_process_id = data.process_id;
                        this.pollUploadProcess();
                    },
                    on_error: (error) => {
                        this.uploading = false;
                        this.upload_errors = [this.formatUploadError ? this.formatUploadError(error.message || error) : (error.message || error)];
                        this.preview_failed = true;
                        this.preview_error_message = 'Upload failed. Please review your file and try again.';
                    }
                }
            });
        },

        /**
         * Get upload URL
         */
        getUploadUrl() {
            return `/api/v1/imports/${this.importType}/upload/${this.$route.params.organization_id}`;
        },

        /**
         * Add additional upload data (override in child components)
         */
        addAdditionalUploadData(/* form_data */) {
            // Default implementation - child components can override
        },

        /**
         * Poll upload process
         */
        pollUploadProcess() {
            this.pollGenericProcess(
                this.upload_process_id,
                this.onUploadComplete.bind(this),
                this.onUploadFailed.bind(this),
                (data) => {
                    this.upload_progress_percent = data?.status?.progress_percent || 0;
                    this.upload_progress_message = data?.status?.progress_message || 'Processing...';
                }
            );
        },

        /**
         * Handle upload completion
         */
        onUploadComplete(result) {
            this.uploading = false;
            this.import_id = result.output.import_id;
            this.import_state = result.output.state;

            // Update URL to include import_id while preserving query parameters
            this.$router.replace({
                name: this.$route.name,
                params: { ...this.$route.params, import_id: this.import_id },
                query: { ...this.$route.query }
            });

            this.loadExistingImport();
        },

        /**
         * Handle upload failure
         */
        onUploadFailed(error) {
            this.uploading = false;
            this.upload_errors = [error.exception || error.message || error];
        },

        /**
         * Handle upload progress updates
         */
        updateUploadProgress(data) {
            // Update progress from ImportProcessProgress component
        },

        // === COLUMN MAPPING FUNCTIONALITY ===

        /**
         * Handle column mapping completion
         */
        onMappingSaved(data) {
            this.import_state = data.state || 'preview-ready';
            this.handleImportStateTransition();
        },

        /**
         * Handle column mapping failure
         */
        onMappingFailed(error) {
            this.$rc.snackbar(`Column mapping failed: ${error}`, 'error');
        },

        /**
         * Handle can continue change
         */
        onCanContinueChanged(canContinue) {
            this.can_continue_to_preview = canContinue;
        },

        // === IMPORT EXECUTION FUNCTIONALITY ===

        /**
         * Execute import
         */
        onExecuteImport() {
            this.importing = true;
            this.import_progress_message = 'Preparing import execution...';
            this.import_progress_percent = 2;

            const requestParams = this.getExecutionRequestParams();

            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.importType}/execute/${this.$route.params.organization_id}/${this.import_id}`,
                'params': requestParams,
                'on_success': (data) => {
                    this.import_process_id = data.process_id;
                    this.pollExecutionProcess(this.import_process_id);
                },
                'on_error': (error) => {
                    this.importing = false;
                    this.import_progress_message = '';
                    this.import_progress_percent = 0;
                    this.execution_errors = [this.formatImportError ? this.formatImportError(error) : (error.message || error)];
                }
            });
        },

        /**
         * Get execution request parameters (override in child components)
         */
        getExecutionRequestParams() {
            return {};
        },

        /**
         * Poll execution process
         */
        pollExecutionProcess(import_process_id) {
            this.pollGenericProcess(
                import_process_id,
                this.onExecutionComplete.bind(this),
                this.onExecutionFailed.bind(this),
                (data) => {
                    this.import_progress_percent = data?.status?.progress_percent || 0;
                    this.import_progress_message = data?.status?.progress_message || 'Processing...';
                }
            );
        },

        /**
         * Handle execution completion
         */
        onExecutionComplete() {
            this.importing = false;
            this.import_state = 'completed';
            this.current_step = 5;
            this.loadExistingImport();
        },

        /**
         * Handle execution failure
         */
        onExecutionFailed(errorMessage) {
            this.importing = false;
            this.execution_errors = [errorMessage || 'Failed to execute import'];
        },

        /**
         * Handle execution progress updates
         */
        updateExecutionProgress(data) {
            // Update progress from ImportProcessProgress component
            this.import_progress_percent = data.progress_percent || 0;
            this.import_progress_message = data.progress_message || 'Processing...';
        },

        // === UTILITY METHODS ===

        /**
         * Cancel import
         */
        cancelImport() {
            this.$rc.rest({
                'endpoint': `POST: /api/v1/imports/${this.importType}/cancel/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': () => {
                    this.import_state = 'cancelled';
                    this.handleImportStateTransition();

                },
                'on_error': (error) => {
                    this.$rc.alert(`Failed to cancel import: ${error}`, 'error');
                }
            });
        },

        /**
         * Format upload error (override in child components)
         */
        formatUploadError(error) {
            return error;
        },

        /**
         * Format import error (override in child components)
         */
        formatImportError(error) {
            return error;
        },

        // === PHONE VALIDATION FUNCTIONALITY (for contact imports) ===

        /**
         * Start phone validation for unknown numbers
         */
        validateUnknownPhoneNumbers() {
            if (this.validating_phone_numbers) {
                return;
            }

            this.validating_phone_numbers = true;
            this.phone_validation_status = 'Starting phone validation...';

            this.$rc.rest({
                'spinner': false,
                'endpoint': `PUT: /api/v1/imports/${this.importType}/validate-endpoints/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    this.phone_validation_batch_id = data.validation_batch_id;
                    this.phone_validation_status = 'Phone validation in progress...';
                    this.startValidationPolling();
                },
                'on_error': (error) => {
                    this.validating_phone_numbers = false;
                    this.phone_validation_status = '';
                    this.phone_validation_status = 'Failed to start phone validation: ' + error;
                    this.$rc.alert(this.phone_validation_status, 'error');
                }
            });
        },

        /**
         * Allow invalid phone numbers by updating intermediate sheet.
         */
        allowInvalidPhoneNumbers() {
            if (this.validating_phone_numbers) {
                return;
            }
            this.validating_phone_numbers = true;
            this.phone_validation_status = 'Allowing invalid phone numbers...';
            this.$rc.rest({
                'spinner': false,
                'endpoint': `PUT: /api/v1/imports/${this.importType}/allow-invalid-phone-numbers/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': () => {
                    // Regenerate preview to reflect bypassed rows
                    this.regenerateIntermediateSheetAfterValidation();
                },
                'on_error': (error) => {
                    this.validating_phone_numbers = false;
                    this.phone_validation_status = '';
                    this.phone_validation_status = 'Failed to allow invalid phone numbers: ' + error;
                    this.$rc.alert(this.phone_validation_status, 'error');
                }
            });
        },


        /**
         * Start polling for validation status
         */
        startValidationPolling() {
            if (this.phone_validation_polling_interval) {
                clearInterval(this.phone_validation_polling_interval);
            }

            this.phone_validation_polling_interval = setInterval(() => {
                this.pollValidationStatus();
            }, 2000);

            // Initial poll
            this.pollValidationStatus();
        },

        /**
         * Poll validation status
         */
        pollValidationStatus() {
            if (!this.phone_validation_batch_id) {
                this.stopValidationPolling();
                return;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': `GET: /api/v1/imports/${this.importType}/validation-status/${this.$route.params.organization_id}/${this.phone_validation_batch_id}`,
                'params': {},
                'on_success': (data) => {
                    if (data.is_complete) {
                        this.onPhoneValidationComplete();
                    } else if (!data.is_active) {
                        this.onPhoneValidationFailed('Phone validation failed or was cancelled');
                    }
                    // Continue polling if still active and not complete
                },
                'on_error': () => {
                    this.onPhoneValidationFailed('Error checking validation status');
                }
            });
        },

        /**
         * Handle phone validation completion
         */
        onPhoneValidationComplete() {
            this.stopValidationPolling();
            this.phone_validation_status = 'Applying validation results and regenerating preview...';

            // Apply validation results to the import
            this.applyValidationResults();
        },

        /**
         * Handle phone validation failure
         */
        onPhoneValidationFailed(message) {
            this.stopValidationPolling();
            this.validating_phone_numbers = false;
            this.phone_validation_status = '';
            this.$rc.alert('Phone validation failed', message);
        },

        /**
         * Stop validation polling
         */
        stopValidationPolling() {
            if (this.phone_validation_polling_interval) {
                clearInterval(this.phone_validation_polling_interval);
                this.phone_validation_polling_interval = null;
            }
        },

        /**
         * Apply validation results to the import
         */
        applyValidationResults() {
            this.used_phone_validation = true;
            this.regenerateIntermediateSheetAfterValidation();

        },

        /**
         * Regenerate intermediate sheet after phone validation (reuses existing polling)
         */
        regenerateIntermediateSheetAfterValidation() {
            this.phone_validation_status = 'Regenerating preview with validation results...';

            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.importType}/create-intermediate/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    // Reuse existing polling pattern like execution polling
                    this.pollGenericProcess(data.process_id,
                        this.onValidationIntermediateSheetComplete.bind(this),
                        (error) => this.onPhoneValidationFailed(`Failed to regenerate preview: ${error}`)
                    );
                },
                'on_error': () => {
                    this.onPhoneValidationFailed('Failed to regenerate preview after validation');
                }
            });
        },

        /**
         * Generic process polling method (reusable for different process types)
         */
        pollGenericProcess(process_id, onSuccess, onError, onProgress = null) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/process/:process_id',
                'params': {
                    'process_id': process_id
                },
                'on_success': (data) => {
                    if (data.state === 'finished') {
                        onSuccess(data);
                    } else if (data.state === 'failed') {
                        onError(data.exception || 'Process failed');
                    } else {
                        // Call progress callback if provided
                        if (onProgress) {
                            onProgress(data);
                        }
                        // Continue polling
                        setTimeout(() => this.pollGenericProcess(process_id, onSuccess, onError, onProgress), 1000);
                    }
                },
                'on_error': (error) => {
                    onError(`Process polling failed: ${error}`);
                }
            });
        },

        /**
         * Handle intermediate sheet generation completion after validation
         */
        onValidationIntermediateSheetComplete() {
            this.validating_phone_numbers = false;
            this.phone_validation_status = 'Phone validation completed successfully!';

            // Reload import metadata and preview data
            this.loadExistingImport();

            // Clear status message after a few seconds
            setTimeout(() => {
                this.phone_validation_status = '';
            }, 3000);
        },

        // === ROW SELECTION METHODS ===

        /**
         * Initialize row selection with all importable rows selected by default
         */
        initializeRowSelection() {
            if (this.preview_data && this.preview_data.preview_rows) {
                // Only select rows that can be imported
                this.selected_rows = this.preview_data.preview_rows.filter(row => row.will_be_imported);
                this.updateSelectAllState();
            }
        },

        /**
         * Toggle selection for a specific row (only if it can be imported)
         */
        toggleRowSelection(item) {
            // Only allow selection if the row can be imported
            if (!item.will_be_imported) {
                return;
            }

            const index = this.selected_rows.findIndex(row => row.row_number === item.row_number);
            if (index > -1) {
                this.selected_rows.splice(index, 1);
            } else {
                this.selected_rows.push(item);
            }
            this.updateSelectAllState();
        },

        /**
         * Toggle select all/none (only importable rows)
         */
        toggleSelectAll() {
            if (this.select_all) {
                this.selected_rows = [];
            } else {
                // Only select rows that can be imported
                this.selected_rows = this.preview_data.preview_rows.filter(row => row.will_be_imported);
            }
            this.updateSelectAllState();
        },

        /**
         * Update the select all state based on current selection
         */
        updateSelectAllState() {
            this.select_all = this.allRowsSelected;
        },

        /**
         * Check if a specific row is selected
         */
        isRowSelected(item) {
            return this.selected_rows.some(row => row.row_number === item.row_number);
        },

        /**
         * Get selected row numbers for API calls
         */
        getSelectedRowNumbers() {
            return this.selected_rows.map(row => row.row_number);
        },

        // === COLUMN MAPPING METHODS ===

        /**
         * Save column mapping and create intermediate sheet
         */
        saveColumnMapping() {
            if (!this.column_mapping_data) {
                this.$rc.snackbar('Please configure column mapping first', 'error');
                return;
            }

            this.saving_column_mapping = true;

            // Save the mapping definition
            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.importType}/update-definition/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {
                    import_definition: this.column_mapping_data
                },
                'on_success': () => {
                    this.createIntermediateSheet();
                },
                'on_error': (error) => {
                    this.saving_column_mapping = false;
                    const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : 'Failed to save column mapping';
                    this.$rc.alert('Failed to save mapping', message);

                }
            });
        },

        /**
         * Create intermediate sheet using generic polling
         */
        createIntermediateSheet() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.importType}/create-intermediate/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    // Use generic polling method
                    this.intermediate_process_id = data.process_id;
                    this.pollGenericProcess(this.intermediate_process_id,
                        this.onIntermediateComplete.bind(this),
                        (error) => this.onIntermediateFailed(error)
                    );
                },
                'on_error': (error) => {
                    this.saving_column_mapping = false;
                    const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : 'Failed to create intermediate sheet';
                    this.intermediate_process_errors = [message];
                    this.current_step = 2;
                    this.$rc.snackbar(`Failed to create intermediate sheet: ${message}`, 'error');
                }
            });
        },

        /**
         * Handle intermediate sheet creation completion
         */
        onIntermediateComplete(data) {
            this.saving_column_mapping = false;
            this.import_state = data.output.state;
            this.onMappingSaved(data.output);
            this.loadExistingImport();
        },

        /**
         * Handle intermediate sheet creation failure
         */
        onIntermediateFailed(error) {
            this.saving_column_mapping = false;
            this.import_state = 'failed';
            const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : (error || 'Failed to create intermediate sheet');
            this.preview_error_message = message;
            this.handleImportStateTransition();
            this.$rc.alert(`Intermediate sheet creation failed`, message);
        },

        /**
         * Handle column mapping data changes from the component
         */
        onColumnMappingChanged(mappingData) {
            this.column_mapping_data = mappingData;
        },

    },
    watch: {
        column_mapping_data: {
            handler(newVal, oldVal) {
                if (!newVal || !oldVal) return;
                if (newVal.has_header_row !== oldVal.has_header_row && this.import_state === 'preview-ready') {
                    this.saveColumnMapping();
                }
            },
            deep: true
        },
        /**
         * Initialize row selection when preview data is loaded
         */
        preview_data: {
            handler(newData) {
                if (newData && newData.preview_rows) {
                    this.initializeRowSelection();
                }
            }
            ,
            immediate: true
        }
        
    },

    beforeDestroy() {
    // Clean up polling when component is destroyed
        this.stopValidationPolling();
    }

};
